import React, { useState } from 'react'
import LockedFill from 'views/components/atoms/Icons/Security/LockedFill'
import { leaseTypes } from 'modules/api/portfolio/constants'
import { portfolioApiSelectors } from 'modules/api/portfolio'
import { getCurrentLeaseType, leaseTypeSelectionEvents } from 'modules/ui/ChatBot'
import { useDispatch, useSelector } from 'react-redux'
import { Button, InfoBox, Radio, RadioGroupCards } from 'views/components'
import styles from './LeaseType.module.scss'

type LeaseTypeFormProps = {
  portfolioId: string
  redirect: (url: string) => void
}

const LeaseTypeForm: React.FC<LeaseTypeFormProps> = ({ portfolioId, redirect }) => {
  const dispatch = useDispatch()
  const [leaseType, setLeaseType] = useState(useSelector(state => getCurrentLeaseType(state)))

  const getIsPortfolioActive = useSelector(portfolioApiSelectors.isActivePortfolio)
  const getIsPortfolioDraft = useSelector(portfolioApiSelectors.isDraftPortfolio)
  const getPropertyIdByPortfolioId = useSelector(portfolioApiSelectors.getPropertyIdByPortfolioId)

  const onLeaseTypeChange = (type: string) => {
    setLeaseType(type)
    dispatch(
      type === leaseTypes.managed
        ? leaseTypeSelectionEvents.managedLeaseSelected()
        : leaseTypeSelectionEvents.unmanagedLeaseSelected(),
    )
  }

  const isPortfolioActive = getIsPortfolioActive(portfolioId)
  const isPortfolioDraft = getIsPortfolioDraft(portfolioId)
  const hasPropertyId = !!getPropertyIdByPortfolioId(portfolioId)

  return (
    <div className={styles.root}>
      {hasPropertyId && (
        <InfoBox type="warning" className={styles['info-box']}>
          <header>
            <LockedFill />
            <p>Lease type cannot be changed after the property has been confirmed.</p>
          </header>
        </InfoBox>
      )}
      <div>
        <RadioGroupCards name="leaseType" defaultSelected={leaseType} onChange={e => onLeaseTypeChange(e.target.value)}>
          <Radio
            name="managed"
            value={leaseTypes.managed}
            label="Managed lease"
            labelDescription="Suitable for properties where rent and communication are managed through reOS."
            features={[
              { label: 'Monthly recurring rent invoice' },
              { label: 'Additional recurring monthly invoices (e.g. utilities)' },
              { label: 'Activation email prompting tenant to make monthly payments through reOS' },
            ]}
            disabled={(isPortfolioActive && leaseType !== leaseTypes.managed) || hasPropertyId}
          />
          <Radio
            name="unmanaged"
            value={leaseTypes.unmanaged}
            label="Unmanaged lease"
            labelDescription="Suitable for properties not actively managed through reOS."
            features={[
              { label: 'Rent invoice for first month only' },
              { label: 'No recurring monthly invoices', included: false },
              { label: 'Activation email prompting tenant to make a once-off payment through reOS' },
            ]}
            disabled={(isPortfolioActive && leaseType !== leaseTypes.unmanaged) || hasPropertyId}
          />
        </RadioGroupCards>
      </div>
      <div className={styles.footer}>
        {isPortfolioDraft && <Button onClick={() => redirect(`/leases/${portfolioId}/edit/property`)}>Next</Button>}
      </div>
    </div>
  )
}

export default LeaseTypeForm
